import type { BaseStorableModel } from './Base';
import type { CalendarEvent } from './Calendar';
import type { Goal } from './Goal';
import type { BaseModel, QueryResult } from './Sqlite';
import type { TranscriptFull } from './Transcript';

export enum TaskStatus {
  TODO = 'TODO',
  IN_PROGRESS = 'IN_PROGRESS',
  DONE = 'DONE',
  CANCELLED = 'CANCELLED'
}

export enum TaskPriority {
  LOW = 'LOW',
  MEDIUM = 'MEDIUM',
  HIGH = 'HIGH',
  URGENT = 'URGENT'
}

export interface Task extends BaseStorableModel {
  id: string;
  name?: string | undefined;
  contentHash?: string;
  description?: string;
  status: TaskStatus;
  priority: TaskPriority | undefined;
  tags: string[]; // Array of tag IDs (references to Tag model)
  createdAt: Date;
  dueDate?: Date;
  updatedAt: Date;
  completedAt?: Date;
  deletedAt?: Date;
  memoryId?: string;
  workspaceId?: string;
  isFavorite?: boolean;
  data?: any; // JSON data
  [key: string]: unknown; // Index signature to satisfy Record<string, unknown> constraint
}

export interface TaskCommonItem extends Task {
  userId: string;
  name: string;
}

export type FormattedTask = {
  id: string;
  description: string;
  href: string;
  assignee: any;
  timeUntilDue: string;
  dueDate: string;
  status: string;
};

export interface TodayTasksGoals {
  tasks: string[];
  events: CalendarEvent[];
  goals: Goal[];
}

export interface TaskModel extends BaseModel<Task, TaskRow> {
  getTasksByTranscriptRaw: (transcriptId: string) => Promise<Task[]>;
  updateItem: (
    data: Partial<Task> & { id: string }
  ) => Promise<QueryResult | undefined>;
  deleteByWorkspace: (workspaceId: string) => Promise<QueryResult>;
  deleteByMemory: (memoryId: string) => Promise<QueryResult>;
  getAllTasksOrdered: () => Promise<Task[]>;
  syncFromTranscripts: (
    transcripts: TranscriptFull[]
  ) => Promise<{ created: number; deleted: number; existing: number }>;
}

// Define the database row type
export type TaskRow = {
  id: string;
  name: string | undefined;
  contentHash?: string;
  description?: string;
  status: string;
  priority: string | undefined;
  tags?: string; // JSON string of tag IDs
  createdAt: string;
  dueDate?: string;
  updatedAt: string;
  completedAt?: string;
  deletedAt?: string;
  memoryId?: string;
  workspaceId?: string;
  isFavorite: string | number | boolean;
  data?: string; // JSON string
};

export type UITask = {
  id: string;
  title: string;
  label: string;
  status: TaskStatus;
  priority: TaskPriority | undefined;
  createdAt: Date;
  updatedAt: Date;
  dueDate?: Date;
  workspace: {
    id: string;
    name: string;
  };
  transcript: {
    id: string;
    name: string;
  };
  weight: number;
  isFavorite?: boolean;
};
